<template>
    <div v-if="loading">
        <Message status="info">{{ $t('loading-project-series') }}</Message>
    </div>

    <div v-if="!loading && projectSeries.length === 0">
        <EmptyState>
            <template #description>
                {{ $t('no-scenario-groups') }}
            </template>
            <template #call-to-action>
                <LinkButton
                    size="Base"
                    variant="Info-Strong"
                    :disabled-explanation="createProjectSeriesDisabledExplanation"
                    :to="getCreateProjectSeriesLink()"
                >
                    <template #icon>
                        <PlusIcon class="size-5" />
                    </template>
                    {{ $t('project-series-create') }}
                </LinkButton>
            </template>
        </EmptyState>
    </div>

    <div v-if="!loading && projectSeries.length > 0" class="space-y-4">
        <ProjectSeriesCard
            v-for="series in seriesWithoutStatus"
            :key="series.id"
            :series="series"
            :is-single-project="series.singleProject"
            :link-target="getProjectSeriesLink(series.id)"
            :link-text="series.nameShort"
        />
        <CollapsibleHeadline
            v-for="[status, groupedSeries] in statusGroups"
            :key="status"
            :heading-level="2"
            :headline-id="status"
            :initial-is-open="status == 'active'"
        >
            <template #headline>
                {{ getProjectSeriesStatusLabel(status) }}
            </template>
            <template #content>
                <div v-if="groupedSeries.length > 0" class="space-y-2">
                    <ProjectSeriesCard
                        v-for="series in groupedSeries"
                        :key="series.id"
                        :series="series"
                        :is-single-project="series.singleProject"
                        :link-target="getProjectSeriesLink(series.id)"
                        :link-text="series.nameShort"
                    ></ProjectSeriesCard>
                </div>
                <div v-else class="font-light text-gray-600">({{ $t('no-project-series-with-this-status') }})</div>
            </template>
        </CollapsibleHeadline>
    </div>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import LinkButton from '@/components/Button/LinkButton.vue';
import CollapsibleHeadline from '@/components/CollapsibleHeadline/CollapsibleHeadline.vue';
import EmptyState from '@/components/EmptyState/EmptyState.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import Message from '@/components/Message/Message.vue';
import { GetAllProjectSeriesDocument } from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed } from 'vue';
import {
    getCreateProjectSeriesLink,
    getProjectSeriesLink,
    getProjectSeriesStatusLabel,
    getSeriesStatus,
} from '../project';
import ProjectSeriesCard from './ProjectSeriesCard.vue';

const fluent = useFluent();

const { result: getProjectSeries, loading } = useQuery(GetAllProjectSeriesDocument);

const groupingStates = ['active', 'completed', 'discontinued'] as const;

const projectSeries = computed(() => getProjectSeries.value?.projectSerieses?.nodes || []);
const mappedSeries = computed(() =>
    projectSeries.value.map((series) => ({
        ...series,
        status: getSeriesStatus(series.projects?.nodes.map((p) => p.status) ?? []),
    })),
);
const seriesWithStatus = computed(() => mappedSeries.value.filter((s) => s.status !== undefined));
const seriesWithoutStatus = computed(() => mappedSeries.value.filter((s) => s.status === undefined));

const statusGroups = computed(() => {
    const groupedObjects = Object.groupBy(seriesWithStatus.value, (series) => series.status ?? '');
    // add map with all states, fill with empty array, if no grouped objects exist
    const stateMap = new Map();
    for (const status of groupingStates) {
        stateMap.set(status, groupedObjects[status] ?? []);
    }
    return stateMap;
});

const createProjectSeriesDisabledExplanation = computed(() =>
    auth.isAdmin() ? undefined : fluent.$t('no-permission-to-create'),
);
</script>
